import { supabase } from '../config/supabase';
import type {
  EventMetrics,
  SeriesMetrics,
  EventAnalyticsRow,
  SeriesAnalyticsRow
} from '../types/organizer-events.types';


/**
 * Service for fetching per-event analytics metrics
 * Uses Supabase RPCs for efficient server-side aggregation
 */
export class EventMetricsService {

  /**
   * Batch load metrics for events by their IDs (optimized - uses cached event IDs)
   * Returns a map of eventId -> EventMetrics
   * @param eventIds - Array of event IDs from the cache
   */
  static async getMetricsByEventIds(
    eventIds: string[]
  ): Promise<Record<string, EventMetrics>> {
    if (!eventIds || eventIds.length === 0) {
      return {};
    }

    try {
      const { data, error } = await (supabase.rpc as any)('get_events_analytics_by_ids', {
        p_event_ids: eventIds
      });

      if (error) {
        console.error('EventMetricsService - getMetricsByEventIds error:', error);
        return {};
      }

      if (!data || !Array.isArray(data)) {
        return {};
      }

      // Convert array to Record<eventId, EventMetrics>
      const metricsMap: Record<string, EventMetrics> = {};

      (data as EventAnalyticsRow[]).forEach((row) => {
        metricsMap[row.event_id] = {
          eventId: row.event_id,
          impressions: Number(row.impressions) || 0,
          clicks: Number(row.clicks) || 0,
          favorites: Number(row.favorites) || 0
        };
      });

      console.log(`EventMetricsService - Loaded metrics for ${Object.keys(metricsMap).length}/${eventIds.length} events`);
      return metricsMap;

    } catch (error) {
      console.error('EventMetricsService - getMetricsByEventIds exception:', error);
      return {};
    }
  }

  /**
   * @deprecated Use getMetricsByEventIds instead (more efficient)
   * Kept for backward compatibility
   */
  static async getOrganizerEventsMetrics(
    organizerId: string
  ): Promise<Record<string, EventMetrics>> {
    try {
      const { data, error } = await (supabase.rpc as any)('get_organizer_events_analytics', {
        p_organizer_id: organizerId
      });

      if (error) {
        console.error('EventMetricsService - getOrganizerEventsMetrics error:', error);
        return {};
      }

      if (!data || !Array.isArray(data)) {
        return {};
      }

      const metricsMap: Record<string, EventMetrics> = {};

      (data as EventAnalyticsRow[]).forEach((row) => {
        metricsMap[row.event_id] = {
          eventId: row.event_id,
          impressions: Number(row.impressions) || 0,
          clicks: Number(row.clicks) || 0,
          favorites: Number(row.favorites) || 0
        };
      });

      console.log(`EventMetricsService - Loaded metrics for ${Object.keys(metricsMap).length} events`);
      return metricsMap;

    } catch (error) {
      console.error('EventMetricsService - getOrganizerEventsMetrics exception:', error);
      return {};
    }
  }

  /**
   * Get metrics for a single event
   * ✅ NOUVEAU : Requête sur 3 niveaux (hot/warm/cold)
   */
  static async getEventMetrics(eventId: string): Promise<EventMetrics | null> {
    try {
      let impressions = 0;
      let clicks = 0;
      let favorites = 0;

      // === Niveau 1 : analytics_events (< 24h) ===
      const { data: eventsData, error: eventsError } = await supabase
        .from('analytics_events')
        .select('event_name')
        .eq('event_id', eventId);

      if (eventsError) {
        console.error('EventMetricsService - getEventMetrics events error:', eventsError);
      } else if (eventsData) {
        eventsData.forEach((row) => {
          switch (row.event_name) {
            case 'apparition_evenement':
              impressions++;
              break;
            case 'clic_evenement':
              clicks++;
              break;
            case 'like_evenement':
              favorites++;
              break;
          }
        });
      }

      // === Niveau 2 : analytics_daily (1-30 jours) ===
      const { data: dailyData, error: dailyError } = await supabase
        .from('analytics_daily')
        .select('apparitions, clics, likes')
        .eq('event_id', eventId);

      if (dailyError) {
        console.error('EventMetricsService - getEventMetrics daily error:', dailyError);
      } else if (dailyData) {
        dailyData.forEach((row: any) => {
          impressions += row.apparitions || 0;
          clicks += row.clics || 0;
          favorites += row.likes || 0;
        });
      }

      // === Niveau 2b : daily - historical series data (event_id=NULL) ===
      const { data: eventData } = await supabase
        .from('events')
        .select('series_id')
        .eq('id', eventId)
        .single();

      if (eventData?.series_id) {
        const seriesId = eventData.series_id;
        // Get count of events in this series for proportional distribution
        const { count: seriesCount } = await supabase
          .from('events')
          .select('*', { count: 'exact', head: true })
          .eq('series_id', seriesId);

        const divisor = Math.max(seriesCount || 1, 1);

        const { data: dailySeriesData, error: dailySeriesError } = await supabase
          .from('analytics_daily')
          .select('apparitions, clics, likes')
          .eq('event_series_id', seriesId)
          .is('event_id', null);

        if (dailySeriesError) {
          console.error('EventMetricsService - getEventMetrics daily series error:', dailySeriesError);
        } else if (dailySeriesData) {
          dailySeriesData.forEach((row: any) => {
            impressions += Math.round((row.apparitions || 0) / divisor);
            clicks += Math.round((row.clics || 0) / divisor);
            favorites += Math.round((row.likes || 0) / divisor);
          });
        }

        // === Niveau 3b : alltime - historical series data ===
        const { data: alltimeSeriesData, error: alltimeSeriesError } = await supabase
          .from('analytics_alltime')
          .select('apparitions, clics, likes')
          .eq('event_series_id', seriesId)
          .is('event_id', null);

        if (alltimeSeriesError) {
          console.error('EventMetricsService - getEventMetrics alltime series error:', alltimeSeriesError);
        } else if (alltimeSeriesData) {
          alltimeSeriesData.forEach((row: any) => {
            impressions += Math.round((Number(row.apparitions) || 0) / divisor);
            clicks += Math.round((Number(row.clics) || 0) / divisor);
            favorites += Math.round((Number(row.likes) || 0) / divisor);
          });
        }
      }

      // === Niveau 3 : analytics_alltime (> 30 jours) ===
      const { data: alltimeData, error: alltimeError } = await supabase
        .from('analytics_alltime')
        .select('apparitions, clics, likes')
        .eq('event_id', eventId);

      if (alltimeError) {
        console.error('EventMetricsService - getEventMetrics alltime error:', alltimeError);
      } else if (alltimeData) {
        alltimeData.forEach((row: any) => {
          impressions += Number(row.apparitions) || 0;
          clicks += Number(row.clics) || 0;
          favorites += Number(row.likes) || 0;
        });
      }

      return {
        eventId,
        impressions,
        clicks,
        favorites
      };

    } catch (error) {
      console.error('EventMetricsService - getEventMetrics exception:', error);
      return null;
    }
  }

  /**
   * Get aggregated metrics for a recurring event series
   */
  static async getSeriesMetrics(seriesId: string): Promise<SeriesMetrics | null> {
    try {
      // Note: RPC not yet in generated types, will be available after migration deployment
      const { data, error } = await (supabase.rpc as any)('get_series_analytics', {
        p_series_id: seriesId
      });

      if (error) {
        console.error('EventMetricsService - getSeriesMetrics error:', error);
        return null;
      }

      if (!data || (Array.isArray(data) && data.length === 0)) {
        return {
          seriesId,
          totalImpressions: 0,
          totalClicks: 0,
          totalFavorites: 0,
          occurrenceCount: 0
        };
      }

      // RPC returns a single row
      const row = Array.isArray(data) ? data[0] as SeriesAnalyticsRow : data as SeriesAnalyticsRow;

      return {
        seriesId,
        totalImpressions: Number(row.total_impressions) || 0,
        totalClicks: Number(row.total_clicks) || 0,
        totalFavorites: Number(row.total_favorites) || 0,
        occurrenceCount: Number(row.occurrence_count) || 0
      };

    } catch (error) {
      console.error('EventMetricsService - getSeriesMetrics exception:', error);
      return null;
    }
  }

}

export default EventMetricsService;
